import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * ErrorIndicator - Displays error state for infinite scroll load-more failures
 * 
 * RQ-ERR-02: Renders in the same position as the loading spinner (sticky, centered)
 * to maintain visibility during horizontal scrolling.
 * 
 * @component
 * @example
 * <ErrorIndicator
 *   message="Failed to load more items"
 *   onRetry={() => refetch()}
 *   retryLabel="Retry"
 * />
 */
const ErrorIndicator = ({ message, onRetry, retryLabel = 'Retry' }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '24px 16px',
                gap: '12px',
                // Sticky positioning to stay visible during horizontal scroll
                // (same behavior as loading indicator and empty state)
                position: 'sticky',
                left: 0,
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'error.main',
                }}
            >
                <ErrorOutlineIcon fontSize="small" />
                <Typography variant="body2" color="error.main">
                    {message}
                </Typography>
            </Box>
            
            {onRetry && (
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={onRetry}
                >
                    {retryLabel}
                </Button>
            )}
        </Box>
    );
};

ErrorIndicator.propTypes = {
    /** Error message to display to the user */
    message: PropTypes.string,
    /** Callback function when user clicks retry button */
    onRetry: PropTypes.func,
    /** Label for the retry button (should be translated) */
    retryLabel: PropTypes.string,
};

ErrorIndicator.defaultProps = {
    message: 'An error occurred while loading more items.',
    retryLabel: 'Retry',
};

export default ErrorIndicator;
